import LinearLightLayer from '../utils/linearLight/layer';
import { isLinearLightBlendMode } from '../utils/linearLight/source';
/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { generatePatternStyles } from '@web-stories-wp/patterns';
import {
  getBox,
  getElementWithSafeZoneOffset,
  FULLBLEED_HEIGHT,
  AMP_DANGER_ZONE_HEIGHT,
  SAFE_AREA_HEIGHT,
  dataToFontSizeY as dataToFontSize,
} from '@web-stories-wp/units';
import { StoryAnimation } from '@web-stories-wp/animation';
import PropTypes from 'prop-types';

/**
 * Internal dependencies
 */
import WithLink from '../components/elementLink/output';
import { getDefinitionForType } from '../elements';
import WithMask from '../masks/output';
import StoryPropTypes from '../types';
import {
  getBorderPositionCSS,
  getBorderRadius,
  getBorderStyle,
  shouldDisplayBorder,
} from '../utils/elementBorder';
import { BACKGROUND_TEXT_MODE } from '../constants';
import { getElementBlendMode } from '../elements/shared/blendModes';
import {
  calcFontMetrics,
  calcVerticalTrimFontMetrics,
  hasMixedTypographyMarkers,
} from '../elements/text/utils';
import { hasSemanticList } from '../elements/text/utils/listStyle';
import getTextHeightResizeUpdates from '../utils/getTextHeightResizeUpdates';
import {
  getOverlayFilterStyle,
  getOverlayRenderStack,
} from '../utils/overlayStack';
import TextBoxBackdrop, {
  TextBoxShadow,
} from '../elements/text/textBoxBackdrop';
import { getTextBoxBackdropStyles } from '../elements/text/getTextBoxBackdropStyles';
import {
  getElementBackdropStyles,
  hasElementBackdrop,
} from '../elements/shared/getElementBackdropStyles';
import { getTextShadowStyles } from '../elements/text/getTextShadowStyles';

function dataToElementPercent(value, basis) {
  return `${basis > 0 ? (value / basis) * 100 : 0}%`;
}

function getTextBoxBackdropAnchorStyle(element) {
  const border = shouldDisplayBorder(element) ? element.border : null;

  return {
    position: 'absolute',
    zIndex: 0,
    pointerEvents: 'none',
    top: border ? `${border.top}px` : 0,
    right: border ? `${border.right}px` : 0,
    bottom: border ? `${border.bottom}px` : 0,
    left: border ? `${border.left}px` : 0,
  };
}

function getElementBackdropAnchorStyle(element) {
  const style = getTextBoxBackdropAnchorStyle(element);
  const { flip } = element;
  return {
    ...style,
    ...(flip?.horizontal ? { left: style.right, right: style.left } : {}),
    ...(flip?.vertical ? { top: style.bottom, bottom: style.top } : {}),
  };
}

const LEADING_ROOT_LINE_BREAK_PATTERN = /^[\t ]*(?:\r\n?|\n)/;

function getLeadingMixedTypographyHeight(element) {
  if (
    !hasMixedTypographyMarkers(element.content || '') ||
    !LEADING_ROOT_LINE_BREAK_PATTERN.test(element.content || '')
  ) {
    return null;
  }

  const metrics = calcFontMetrics({
    element,
    dataToEditorX: (value) => value,
    dataToEditorY: (value) => value,
    calculateStandardTrimHeight: true,
  });

  return (
    (element.verticalTrim === 'baseline'
      ? metrics.height
      : metrics.standardTrimHeight) ?? null
  );
}

function getSupportedTextElement(element) {
  if (element.type !== 'text') {
    return null;
  }

  return element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT &&
    hasSemanticList(element.content)
    ? { ...element, backgroundTextMode: BACKGROUND_TEXT_MODE.FILL }
    : element;
}

function OutputElementContent({
  animations = [],
  element: _element,
  isPageNormalized = false,
}) {
  const elementWithSafeZoneOffset = getElementWithSafeZoneOffset(_element);
  const element = isPageNormalized
    ? elementWithSafeZoneOffset
    : getTextBoxAdjustedElement(elementWithSafeZoneOffset);
  const {
    id,
    blendMode,
    opacity,
    type,
    border,
    backgroundColor,
    isBackground,
    isHidden,
  } = element;
  const overlayFilters = getOverlayRenderStack(element);

  if (isHidden) {
    return null;
  }

  const { Output } = getDefinitionForType(type);
  const supportedTextElement = getSupportedTextElement(element);

  // Box is calculated based on the 100%:100% basis for width and height
  const box = getBox(
    element,
    100,
    100,
    true /** withSafeArea */,
    FULLBLEED_HEIGHT,
    AMP_DANGER_ZONE_HEIGHT
  );

  const { x, y, width, height, rotationAngle } = box;
  const overlayBox = {
    height: element.height,
    width: element.width,
  };
  const elementBlendMode = getElementBlendMode(blendMode);
  const textBoxBackdropStyle = supportedTextElement
    ? getTextBoxBackdropStyles({
        element: supportedTextElement,
        dataToStyleX: (value) => dataToElementPercent(value, element.width),
        dataToStyleY: (value) => dataToElementPercent(value, element.height),
        dataToBlur: (value) =>
          `${dataToFontSize(value, 100, true /** withSafeArea */)}em`,
        disableBlur:
          Boolean(elementBlendMode) ||
          isLinearLightBlendMode(element.blendMode),
      })
    : null;
  const elementBackdropStyle = getElementBackdropStyles({
    element,
    dataToStyleX: (value) => dataToElementPercent(value, element.width),
    dataToStyleY: (value) => dataToElementPercent(value, element.height),
    dataToBlur: (value) => `${dataToFontSize(value, 100, true)}em`,
  });
  const hasBackdrop = Boolean(textBoxBackdropStyle || elementBackdropStyle);
  const textBoxShadowStyle = supportedTextElement
    ? getTextShadowStyles({ element: supportedTextElement })
    : null;
  const backdropCompositeStyle = hasBackdrop
    ? {
        ...(typeof opacity !== 'undefined' ? { opacity: opacity / 100 } : null),
        ...(elementBlendMode ? { mixBlendMode: elementBlendMode } : null),
      }
    : null;

  // We're adding background styles in case of Fill here so that
  // the background and the border would match together.
  const bgStyles = {
    backgroundClip: 'content-box',
    ...generatePatternStyles(backgroundColor, 'background', null, element),
  };

  return (
    <div
      style={{
        position: 'absolute',
        pointerEvents: 'none',
        left: `${x}%`,
        top: `${y}%`,
        width: `${width}%`,
        height: `${height}%`,
        ...(shouldDisplayBorder(element)
          ? getBorderPositionCSS({
              ...border,
              width: `${width}%`,
              height: `${height}%`,
              posTop: `${y}%`,
              posLeft: `${x}%`,
            })
          : null),
        transform: rotationAngle ? `rotate(${rotationAngle}deg)` : null,
        ...(!hasBackdrop && elementBlendMode
          ? { mixBlendMode: elementBlendMode }
          : null),
        opacity:
          !hasBackdrop && typeof opacity !== 'undefined' ? opacity / 100 : null,
      }}
    >
      <StoryAnimation.AMPWrapper animations={animations} target={id}>
        {textBoxBackdropStyle && (
          <>
            <TextBoxShadow
              style={
                textBoxShadowStyle?.boxShadow
                  ? {
                      inset: 0,
                      ...getBorderRadius(element),
                      ...backdropCompositeStyle,
                      boxShadow: textBoxShadowStyle.boxShadow,
                    }
                  : null
              }
            />
            <div
              className="text-box-backdrop-anchor"
              style={getTextBoxBackdropAnchorStyle(element)}
            >
              <TextBoxBackdrop
                element={supportedTextElement}
                style={{
                  ...textBoxBackdropStyle,
                  ...backdropCompositeStyle,
                }}
              />
            </div>
          </>
        )}
        {elementBackdropStyle && (
          <div
            className="element-backdrop-anchor"
            style={getElementBackdropAnchorStyle(element)}
          >
            <div
              aria-hidden="true"
              className="element-backdrop"
              style={elementBackdropStyle}
            />
          </div>
        )}
        <WithMask
          data-combined-paint-frame={
            shouldDisplayBorder(element) ? '' : undefined
          }
          className={element.type === 'text' ? undefined : 'mask'}
          element={element}
          box={box}
          id={'el-' + id}
          style={{
            ...(shouldDisplayBorder(element) ? getBorderStyle(element) : null),
            pointerEvents: 'initial',
            width: '100%',
            height: '100%',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 0,
            ...getBorderRadius(element),
            ...(hasBackdrop ? backdropCompositeStyle : null),
            ...(supportedTextElement?.backgroundTextMode ===
              BACKGROUND_TEXT_MODE.FILL && !hasBackdrop
              ? {
                  ...bgStyles,
                  ...(textBoxShadowStyle?.boxShadow
                    ? { boxShadow: textBoxShadowStyle.boxShadow }
                    : null),
                }
              : null),
          }}
          skipDefaultMask
        >
          <WithLink
            element={element}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <Output element={element} box={box} />
          </WithLink>
          {!isBackground &&
            overlayFilters.map((filter) => (
              <div
                key={filter.id}
                className="element-overlay-area"
                style={getOverlayFilterStyle(filter, overlayBox)}
              />
            ))}
        </WithMask>
      </StoryAnimation.AMPWrapper>
    </div>
  );
}

function OutputElement(props) {
  const { element } = props;
  if (
    (hasElementBackdrop(element) ||
      isLinearLightBlendMode(element.blendMode)) &&
    ['image', 'text', 'shape'].includes(element.type) &&
    !element.isHidden
  ) {
    return (
      <LinearLightLayer
        combinedBlur={hasElementBackdrop(element)}
        blendMode={element.blendMode}
        opacity={element.opacity}
        paddingRatio={AMP_DANGER_ZONE_HEIGHT / SAFE_AREA_HEIGHT}
      >
        <OutputElementContent
          {...props}
          element={{
            ...element,
            opacity: 100,
            blendMode: hasElementBackdrop(element)
              ? 'normal'
              : element.blendMode,
          }}
        />
      </LinearLightLayer>
    );
  }
  return <OutputElementContent {...props} />;
}

OutputElement.propTypes = {
  element: StoryPropTypes.element.isRequired,
  isPageNormalized: PropTypes.bool,
};

export default OutputElement;

export function getTextBoxAdjustedElement(element) {
  const hasTextBox =
    element.backgroundTextMode &&
    element.backgroundTextMode !== BACKGROUND_TEXT_MODE.NONE;

  if (element.type !== 'text') {
    return element;
  }

  const leadingMixedTypographyHeight = getLeadingMixedTypographyHeight(element);
  const shouldNormalizeBaselineTextBox =
    element.verticalTrim === 'baseline' && hasTextBox && element.font?.metrics;

  if (
    leadingMixedTypographyHeight === null &&
    !shouldNormalizeBaselineTextBox
  ) {
    return element;
  }

  const height =
    leadingMixedTypographyHeight ??
    calcVerticalTrimFontMetrics({
      element,
      dataToEditorX: (value) => value,
      dataToEditorY: (value) => value,
    }).height;

  if (!height || Math.abs(height - element.height) < 0.5) {
    return element;
  }

  return {
    ...element,
    ...getTextHeightResizeUpdates(element, height),
  };
}
